import {
  assignableRoles,
  createUserRequest,
  ROLES_BY_KIND,
  type Role,
  type UserCreatedResponse,
  type UserDto,
  type UserKind,
} from '@zenarty/shared';
import {
  KeyRound,
  Pencil,
  Plus,
  ShieldCheck,
  UserCheck,
  UserPlus,
  Users as UsersIcon,
  UserX,
} from 'lucide-react';
import { useMemo, useState, type ReactNode } from 'react';
import { useForm } from 'react-hook-form';
import { api } from '../api';
import { useAuth } from '../auth';
import { EntityHistory } from '../components/audit';
import {
  Avatar,
  Badge,
  Button,
  Callout,
  EmptyState,
  Field,
  KeyValue,
  OneTimeSecret,
  PageHeader,
  StatusBadge,
  Tabs,
} from '../components/kit';
import { ConfirmDialog, Dialog, Drawer } from '../components/overlays';
import { DataTable, type Column } from '../components/table';
import { formatDateTime, timeAgo } from '../lib/format';
import { nameMap, useAction, useClients, useSites, useUsers } from '../lib/queries';

export const ROLE_LABEL: Record<Role, string> = {
  operator_admin: 'Operator admin',
  site_manager: 'Site manager',
  supervisor: 'Supervisor',
  associate: 'Associate',
  billing: 'Billing',
  client_admin: 'Client admin',
  client_user: 'Client user',
};

const ROLE_HELP: Record<Role, string> = {
  operator_admin: 'Full control of sites, clients, users, scanners and the audit log',
  site_manager: 'Runs their sites: manages floor staff and scanners, reads the audit log',
  supervisor: 'Read access to sites, clients and people in scope; directs floor work',
  associate: 'Floor work on a scanner',
  billing: 'Invoicing and client commercial data',
  client_admin: 'Client portal: manages their own colleagues',
  client_user: 'Client portal: their own stock, orders and documents',
};

const KIND_LABEL: Record<UserKind, string> = {
  office: 'Office',
  floor: 'Floor',
  client: 'Client portal',
};

type TabId = 'all' | UserKind | 'disabled';

function twoStep(u: UserDto): ReactNode {
  if (u.kind === 'floor') return <Badge>Badge + PIN</Badge>;
  return u.mfaEnrolled ? (
    <Badge tone="ok" dot>
      Enrolled
    </Badge>
  ) : (
    <Badge tone="warn" dot>
      At next sign-in
    </Badge>
  );
}

export function UsersPage() {
  const { can, me } = useAuth();
  const users = useUsers();
  const sites = useSites(can('site:read'));
  const clients = useClients(can('client:read'));
  const [tab, setTab] = useState<TabId>('all');
  const [creating, setCreating] = useState(false);
  const [secret, setSecret] = useState<UserCreatedResponse | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  const siteCode = nameMap(sites.data?.items, (s) => s.code);
  const clientName = nameMap(clients.data?.items, (c) => c.name);

  const all = useMemo(() => users.data?.items ?? [], [users.data]);
  const counts = useMemo(
    () => ({
      all: all.filter((u) => u.status === 'active').length,
      office: all.filter((u) => u.kind === 'office' && u.status === 'active').length,
      floor: all.filter((u) => u.kind === 'floor' && u.status === 'active').length,
      client: all.filter((u) => u.kind === 'client' && u.status === 'active').length,
      disabled: all.filter((u) => u.status === 'disabled').length,
    }),
    [all],
  );
  const rows = all.filter((u) =>
    tab === 'disabled'
      ? u.status === 'disabled'
      : u.status === 'active' && (tab === 'all' || u.kind === tab),
  );

  const scope = (u: UserDto) => {
    if (u.kind === 'client') return u.clientId ? (clientName.get(u.clientId) ?? 'Client') : '—';
    const s = u.allSites
      ? 'All sites'
      : u.siteIds.map((id) => siteCode.get(id) ?? '…').join(', ') || 'No sites';
    const c = u.allClients
      ? 'all clients'
      : `${u.clientIds.length} client${u.clientIds.length === 1 ? '' : 's'}`;
    return `${s} · ${c}`;
  };

  const columns: Column<UserDto>[] = [
    {
      id: 'name',
      header: 'Person',
      sortValue: (u) => u.name,
      cell: (u) => (
        <div className="row" style={{ flexWrap: 'nowrap' }}>
          <Avatar name={u.name} />
          <div style={{ minWidth: 0 }}>
            <div className="primary-cell">
              {u.name} {u.id === me?.id ? <Badge tone="accent">You</Badge> : null}
            </div>
            <div className="sub">{u.email ?? `Badge ${u.badgeNo ?? ''}`}</div>
          </div>
        </div>
      ),
    },
    {
      id: 'kind',
      header: 'Type',
      sortValue: (u) => u.kind,
      cell: (u) => <span className="tag">{KIND_LABEL[u.kind]}</span>,
    },
    {
      id: 'roles',
      header: 'Role',
      sortValue: (u) => u.roles.join(),
      cell: (u) => u.roles.map((r) => ROLE_LABEL[r]).join(', '),
    },
    { id: 'scope', header: 'Access', cell: (u) => <span className="muted">{scope(u)}</span> },
    {
      id: 'mfa',
      header: 'Sign-in security',
      sortValue: (u) => (u.kind === 'floor' ? 2 : u.mfaEnrolled ? 1 : 0),
      cell: twoStep,
    },
    {
      id: 'last',
      header: 'Last sign-in',
      sortValue: (u) => u.lastLoginAt ?? '',
      cell: (u) => (
        <span className="muted" title={formatDateTime(u.lastLoginAt)}>
          {timeAgo(u.lastLoginAt)}
        </span>
      ),
    },
  ];
  if (tab === 'disabled')
    columns.push({
      id: 'status',
      header: 'Status',
      cell: (u) => <StatusBadge status={u.status} />,
    });

  const open = all.find((u) => u.id === openId) ?? null;
  return (
    <>
      <PageHeader
        title="Users & access"
        description="Everyone who can sign in: office staff on the web, floor staff on scanners, and your clients' portal users."
        actions={
          can('user:manage') ? (
            <Button variant="primary" icon={UserPlus} onClick={() => setCreating(true)}>
              Add person
            </Button>
          ) : null
        }
      />
      {secret ? <CredentialNotice result={secret} onDismiss={() => setSecret(null)} /> : null}
      <Tabs
        value={tab}
        onChange={setTab}
        tabs={[
          { id: 'all', label: 'Active', count: counts.all },
          { id: 'office', label: 'Office', count: counts.office },
          { id: 'floor', label: 'Floor', count: counts.floor },
          { id: 'client', label: 'Client portal', count: counts.client },
          { id: 'disabled', label: 'Disabled', count: counts.disabled },
        ]}
      />
      <DataTable
        columns={columns}
        rows={rows}
        rowKey={(u) => u.id}
        loading={users.isLoading}
        searchPlaceholder="Search by name, email or badge"
        searchText={(u) => `${u.name} ${u.email ?? ''} ${u.badgeNo ?? ''} ${u.roles.join(' ')}`}
        onRowClick={(u) => setOpenId(u.id)}
        initialSort={{ id: 'name' }}
        empty={
          <EmptyState
            icon={UsersIcon}
            title={tab === 'disabled' ? 'No disabled accounts' : 'Nobody here yet'}
          />
        }
      />
      {creating ? (
        <CreateUserDialog
          onClose={() => setCreating(false)}
          onCreated={(r) => {
            setSecret(r);
            setCreating(false);
          }}
        />
      ) : null}
      {open ? (
        <UserDrawer user={open} onClose={() => setOpenId(null)} onSecret={setSecret} />
      ) : null}
    </>
  );
}

function CredentialNotice({
  result,
  onDismiss,
}: {
  result: UserCreatedResponse;
  onDismiss: () => void;
}) {
  const who = result.user.email ?? result.user.name;
  return (
    <div className="stack-sm" style={{ marginBottom: 16 }}>
      {result.pin ? (
        <OneTimeSecret
          label={`Scanner PIN for ${result.user.name} (badge ${result.user.badgeNo})`}
          value={result.pin}
        />
      ) : result.temporaryPassword ? (
        <OneTimeSecret
          label={`One-time password for ${who}`}
          value={result.temporaryPassword}
          note="Shown only once. They must replace it and set up two-step sign-in when they first sign in."
        />
      ) : null}
      <div>
        <Button size="sm" variant="ghost" onClick={onDismiss}>
          Dismiss
        </Button>
      </div>
    </div>
  );
}

function UserDrawer({
  user,
  onClose,
  onSecret,
}: {
  user: UserDto;
  onClose: () => void;
  onSecret: (r: UserCreatedResponse) => void;
}) {
  const { can, me } = useAuth();
  const sites = useSites(can('site:read'));
  const clients = useClients(can('client:read'));
  const [dialog, setDialog] = useState<'roles' | 'access' | 'reset' | 'status' | null>(null);
  const self = user.id === me?.id;
  const manageable =
    can('user:manage') && !self && user.roles.every((r) => assignableRoles(me?.roles ?? []).has(r));
  const siteName = nameMap(sites.data?.items, (s) => `${s.name} (${s.code})`);
  const clientName = nameMap(clients.data?.items, (c) => c.name);

  const reset = useAction(
    () => api<UserCreatedResponse>(`/users/${user.id}/reset-credential`, { method: 'POST' }),
    {
      invalidate: [['users']],
      success: user.kind === 'floor' ? 'New PIN issued' : 'One-time password issued',
      onSuccess: (r) => {
        setDialog(null);
        onSecret(r);
        onClose();
      },
    },
  );
  const status = useAction(
    () =>
      api(`/users/${user.id}/${user.status === 'active' ? 'disable' : 'enable'}`, {
        method: 'POST',
      }),
    {
      invalidate: [['users']],
      success:
        user.status === 'active'
          ? `${user.name} can no longer sign in`
          : `${user.name} can sign in again`,
      onSuccess: () => setDialog(null),
    },
  );

  return (
    <Drawer
      title={user.name}
      subtitle={user.email ?? `Badge ${user.badgeNo ?? ''}`}
      icon={<Avatar name={user.name} size="lg" />}
      onClose={onClose}
      footer={
        manageable ? (
          <>
            <Button icon={ShieldCheck} onClick={() => setDialog('roles')}>
              Role
            </Button>
            {user.kind !== 'client' ? (
              <Button icon={Pencil} onClick={() => setDialog('access')}>
                Access
              </Button>
            ) : null}
            <Button icon={KeyRound} onClick={() => setDialog('reset')}>
              {user.kind === 'floor' ? 'Reset PIN' : 'Reset password'}
            </Button>
            <Button
              variant={user.status === 'active' ? 'danger' : 'default'}
              icon={user.status === 'active' ? UserX : UserCheck}
              onClick={() => setDialog('status')}
            >
              {user.status === 'active' ? 'Disable' : 'Enable'}
            </Button>
          </>
        ) : self ? (
          <span className="muted small">
            You can't change your own access. Ask another administrator.
          </span>
        ) : null
      }
    >
      <section>
        <div className="section-title">Account</div>
        <KeyValue
          items={[
            ['Status', <StatusBadge key="s" status={user.status} />],
            ['Type', KIND_LABEL[user.kind]],
            ['Role', user.roles.map((r) => ROLE_LABEL[r]).join(', ')],
            ['Sign-in security', twoStep(user)],
            [
              'Last sign-in',
              user.lastLoginAt
                ? `${formatDateTime(user.lastLoginAt)} (${timeAgo(user.lastLoginAt)})`
                : 'Never',
            ],
            ['Created', formatDateTime(user.createdAt)],
          ]}
        />
      </section>
      <section>
        <div className="section-title">Access scope</div>
        {user.kind === 'client' ? (
          <p>
            Client portal for{' '}
            <strong>
              {user.clientId ? (clientName.get(user.clientId) ?? 'their client') : '—'}
            </strong>{' '}
            only.
          </p>
        ) : (
          <KeyValue
            items={[
              [
                'Sites',
                user.allSites ? (
                  <Badge key="a" tone="accent">
                    All sites
                  </Badge>
                ) : (
                  <div key="s" className="row">
                    {user.siteIds.map((id) => (
                      <span key={id} className="tag">
                        {siteName.get(id) ?? 'Site'}
                      </span>
                    ))}
                    {user.siteIds.length ? null : <span className="faint">None</span>}
                  </div>
                ),
              ],
              [
                'Clients',
                user.allClients ? (
                  <Badge key="a" tone="accent">
                    All clients
                  </Badge>
                ) : (
                  <div key="c" className="row">
                    {user.clientIds.map((id) => (
                      <span key={id} className="tag">
                        {clientName.get(id) ?? 'Client'}
                      </span>
                    ))}
                    {user.clientIds.length ? null : <span className="faint">None</span>}
                  </div>
                ),
              ],
            ]}
          />
        )}
      </section>
      {can('audit:read') ? (
        <section>
          <div className="section-title">History</div>
          <EntityHistory entityId={user.id} />
        </section>
      ) : null}

      {dialog === 'roles' ? <RolesDialog user={user} onClose={() => setDialog(null)} /> : null}
      {dialog === 'access' ? <AccessDialog user={user} onClose={() => setDialog(null)} /> : null}
      {dialog === 'reset' ? (
        <ConfirmDialog
          title={
            user.kind === 'floor'
              ? `Issue a new PIN for ${user.name}?`
              : `Reset ${user.name}'s password?`
          }
          confirmLabel={user.kind === 'floor' ? 'Issue new PIN' : 'Reset password'}
          pending={reset.isPending}
          onConfirm={() => reset.mutate(undefined)}
          onClose={() => setDialog(null)}
        >
          {user.kind === 'floor'
            ? 'Their current PIN stops working immediately. You will see the new PIN once.'
            : 'Their current password stops working. You will see a one-time password once; they must change it at next sign-in.'}
        </ConfirmDialog>
      ) : null}
      {dialog === 'status' ? (
        <ConfirmDialog
          title={user.status === 'active' ? `Disable ${user.name}?` : `Enable ${user.name}?`}
          confirmLabel={user.status === 'active' ? 'Disable access' : 'Enable access'}
          danger={user.status === 'active'}
          pending={status.isPending}
          onConfirm={() => status.mutate(undefined)}
          onClose={() => setDialog(null)}
        >
          {user.status === 'active'
            ? 'They are signed out everywhere at once, including scanners. Their history stays in the audit log.'
            : 'They can sign in again with their existing credentials.'}
        </ConfirmDialog>
      ) : null}
    </Drawer>
  );
}

function RoleChoices({
  kind,
  value,
  onChange,
}: {
  kind: UserKind;
  value: Role[];
  onChange: (r: Role[]) => void;
}) {
  const { me } = useAuth();
  const grantable = assignableRoles(me?.roles ?? []);
  const options = ROLES_BY_KIND[kind].filter((r) => grantable.has(r));
  if (!options.length)
    return <Callout tone="warn" title="You can't grant roles to this type of person." />;
  return (
    <div className="stack-sm">
      {options.map((r) => (
        <label key={r} className="check" style={{ alignItems: 'flex-start' }}>
          <input
            type="checkbox"
            checked={value.includes(r)}
            onChange={(e) =>
              onChange(e.target.checked ? [...value, r] : value.filter((x) => x !== r))
            }
            style={{ marginTop: 2 }}
          />
          <span>
            <strong>{ROLE_LABEL[r]}</strong>
            <span className="muted small" style={{ display: 'block' }}>
              {ROLE_HELP[r]}
            </span>
          </span>
        </label>
      ))}
    </div>
  );
}

function RolesDialog({ user, onClose }: { user: UserDto; onClose: () => void }) {
  const [roles, setRoles] = useState<Role[]>(user.roles);
  const save = useAction(
    () => api(`/users/${user.id}/roles`, { method: 'POST', body: { roles } }),
    {
      invalidate: [['users']],
      success: 'Role updated',
      onSuccess: onClose,
    },
  );
  return (
    <Dialog
      title={`Role for ${user.name}`}
      description="Roles decide what someone can do; access scope decides where."
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            disabled={!roles.length}
            loading={save.isPending}
            onClick={() => save.mutate(undefined)}
          >
            Save role
          </Button>
        </>
      }
    >
      <RoleChoices kind={user.kind} value={roles} onChange={setRoles} />
    </Dialog>
  );
}

interface ScopeValue {
  allSites: boolean;
  siteIds: string[];
  allClients: boolean;
  clientIds: string[];
}

function ScopeChoices({
  value,
  onChange,
}: {
  value: ScopeValue;
  onChange: (v: ScopeValue) => void;
}) {
  const { me, can } = useAuth();
  const sites = useSites(can('site:read'));
  const clients = useClients(can('client:read'));
  const siteOptions = (sites.data?.items ?? []).filter(
    (s) => me?.allSites || me?.siteIds.includes(s.id),
  );
  const clientOptions = (clients.data?.items ?? []).filter(
    (c) => me?.allClients || me?.clientIds.includes(c.id),
  );
  const toggle = (list: string[], id: string, on: boolean) =>
    on ? [...list, id] : list.filter((x) => x !== id);
  return (
    <div className="form-grid">
      <div>
        <div className="fieldset-title">Sites</div>
        <div className="choice-list">
          {me?.allSites ? (
            <label className="check">
              <input
                type="checkbox"
                checked={value.allSites}
                onChange={(e) => onChange({ ...value, allSites: e.target.checked })}
              />{' '}
              <strong>All sites</strong>, including future ones
            </label>
          ) : null}
          {!value.allSites &&
            siteOptions.map((s) => (
              <label key={s.id} className="check">
                <input
                  type="checkbox"
                  checked={value.siteIds.includes(s.id)}
                  onChange={(e) =>
                    onChange({ ...value, siteIds: toggle(value.siteIds, s.id, e.target.checked) })
                  }
                />{' '}
                {s.name}
              </label>
            ))}
        </div>
      </div>
      <div>
        <div className="fieldset-title">Clients</div>
        <div className="choice-list">
          {me?.allClients ? (
            <label className="check">
              <input
                type="checkbox"
                checked={value.allClients}
                onChange={(e) => onChange({ ...value, allClients: e.target.checked })}
              />{' '}
              <strong>All clients</strong>, including future ones
            </label>
          ) : null}
          {!value.allClients &&
            clientOptions.map((c) => (
              <label key={c.id} className="check">
                <input
                  type="checkbox"
                  checked={value.clientIds.includes(c.id)}
                  onChange={(e) =>
                    onChange({
                      ...value,
                      clientIds: toggle(value.clientIds, c.id, e.target.checked),
                    })
                  }
                />{' '}
                {c.name}
              </label>
            ))}
        </div>
      </div>
    </div>
  );
}

function AccessDialog({ user, onClose }: { user: UserDto; onClose: () => void }) {
  const [scope, setScope] = useState<ScopeValue>({
    allSites: user.allSites,
    siteIds: user.siteIds,
    allClients: user.allClients,
    clientIds: user.clientIds,
  });
  const save = useAction(() => api(`/users/${user.id}`, { method: 'PATCH', body: scope }), {
    invalidate: [['users']],
    success: 'Access updated',
    onSuccess: onClose,
  });
  return (
    <Dialog
      title={`Access for ${user.name}`}
      description="Which sites and clients this person can see and work on. You can only grant what you have yourself."
      onClose={onClose}
      wide
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" loading={save.isPending} onClick={() => save.mutate(undefined)}>
            Save access
          </Button>
        </>
      }
    >
      <ScopeChoices value={scope} onChange={setScope} />
    </Dialog>
  );
}

interface CreateForm {
  name: string;
  email: string;
  badgeNo: string;
  clientId: string;
}

function CreateUserDialog({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (r: UserCreatedResponse) => void;
}) {
  const { me, can } = useAuth();
  const clients = useClients(can('client:read'));
  const isClientAdmin = me?.kind === 'client';
  const [kind, setKind] = useState<UserKind>(isClientAdmin ? 'client' : 'office');
  const [roles, setRoles] = useState<Role[]>([]);
  const [scope, setScope] = useState<ScopeValue>({
    allSites: false,
    siteIds: [],
    allClients: Boolean(me?.allClients),
    clientIds: [],
  });
  const [problems, setProblems] = useState<string[]>([]);
  const form = useForm<CreateForm>({
    defaultValues: { name: '', email: '', badgeNo: '', clientId: me?.clientId ?? '' },
  });

  const create = useAction(
    (body: unknown) => api<UserCreatedResponse>('/users', { method: 'POST', body }),
    {
      invalidate: [['users']],
      success: (r) => `${r.user.name} added`,
      onSuccess: onCreated,
    },
  );

  const submit = form.handleSubmit((v) => {
    const s = {
      ...scope,
      siteIds: scope.allSites ? [] : scope.siteIds,
      clientIds: scope.allClients ? [] : scope.clientIds,
    };
    const body =
      kind === 'client'
        ? { kind, email: v.email, name: v.name, clientId: v.clientId, roles }
        : kind === 'office'
          ? { kind, email: v.email, name: v.name, roles, ...s }
          : { kind, badgeNo: v.badgeNo, name: v.name, roles, ...s };
    const parsed = createUserRequest.safeParse(body);
    if (!parsed.success) {
      setProblems(parsed.error.issues.map((i) => `${i.path.join('.') || 'form'}: ${i.message}`));
      return;
    }
    setProblems([]);
    create.mutate(parsed.data);
  });

  return (
    <Dialog
      title="Add a person"
      description="They receive a one-time password (office and client users) or a scanner PIN (floor staff), shown to you once."
      onClose={onClose}
      onSubmit={(e) => void submit(e)}
      wide
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="primary" icon={Plus} loading={create.isPending}>
            Add person
          </Button>
        </>
      }
    >
      {problems.length ? (
        <Callout tone="danger" title="Check these fields">
          <ul>
            {problems.map((p) => (
              <li key={p}>{p}</li>
            ))}
          </ul>
        </Callout>
      ) : null}
      {!isClientAdmin ? (
        <div className="segmented" role="radiogroup" aria-label="Type of person">
          {(['office', 'floor', 'client'] as UserKind[]).map((k) => (
            <button
              key={k}
              type="button"
              role="radio"
              aria-checked={kind === k}
              className={kind === k ? 'active' : ''}
              onClick={() => {
                setKind(k);
                setRoles([]);
              }}
            >
              {k === 'office'
                ? 'Office staff'
                : k === 'floor'
                  ? 'Floor staff'
                  : 'Client portal user'}
            </button>
          ))}
        </div>
      ) : null}
      <div className="form-grid">
        <Field label="Full name">
          <input className="input" autoFocus {...form.register('name')} />
        </Field>
        {kind === 'floor' ? (
          <Field label="Badge number" hint="Printed on their badge; used with a PIN on scanners">
            <input className="input mono" {...form.register('badgeNo')} />
          </Field>
        ) : (
          <Field label="Work email" hint="Used to sign in">
            <input className="input" type="email" {...form.register('email')} />
          </Field>
        )}
        {kind === 'client' && !isClientAdmin ? (
          <Field label="Client" className="span-2">
            <select className="select" {...form.register('clientId')}>
              <option value="">Choose the client they work for…</option>
              {(clients.data?.items ?? []).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </Field>
        ) : null}
      </div>
      <div>
        <div className="fieldset-title">Role</div>
        <RoleChoices kind={kind} value={roles} onChange={setRoles} />
      </div>
      {kind !== 'client' ? (
        <div>
          <div className="fieldset-title">Access scope</div>
          <ScopeChoices value={scope} onChange={setScope} />
        </div>
      ) : null}
    </Dialog>
  );
}
